<template>
    <div
        class="bo-popup-modal-layer bo-modal-layer"
        :class="{ 'appended-to-body': appendToBody }"
        data-bo-modal-layer
        :style="{ zIndex: layerZIndex }"
    >
        <div
            class="modal-backdrop fade show"
            v-if="fade && visible"
        ></div>
        <div
            v-if="visible"
            :id="modalId"
            :class="['modal bo-modal', modalName]"
            tabindex="-1"
            role="dialog"
            :aria-labelledby="noTitle ? null : modalTitleId"
            :aria-label="noTitle && title ? title : null"
            :aria-hidden="visible ? 'false' : 'true'"
            :aria-modal="visible ? 'true' : null"
            style="display: block"
        >
            <div
                :class="['success modal-dialog modal-dialog-centered', modalStyle]"
                role="document"
            >
                <div class="modal-content">
                    <div
                        v-if="!noTitle"
                        class="modal-header"
                        :class="[{ bordered: bordered }]"
                    >
                        <div class="left-panel">
                            <slot name="header-content">
                                <div
                                    :id="modalTitleId"
                                    class="modal-title"
                                >
                                    {{ title }}
                                </div>
                            </slot>
                        </div>
                        <div class="right-panel">
                            <slot name="right-header"></slot>
                            <i
                                v-if="!hideCloseButton"
                                class="icon-close cursor-pointer"
                                @click="deactivate"
                            ></i>
                        </div>
                    </div>

                    <div v-if="showModalBanner">
                        <slot name="banner-content"></slot>
                    </div>

                    <div class="modal-body">
                        <slot name="body-content"></slot>
                    </div>

                    <div
                        class="modal-footer"
                        v-if="showModalFooter"
                    >
                        <div class="footer-content">
                            <div class="side-info">
                                <slot name="side-info"></slot>
                            </div>

                            <div
                                class="button-groups"
                                v-if="showModalFooterButton"
                            >
                                <slot name="button"></slot>
                                <button
                                    v-if="(hideCloseButton || showCancel) && !hideCancelButton"
                                    type="button"
                                    id="cancel_button"
                                    class="btn-rounded light"
                                    @click="deactivate"
                                >
                                    <span>{{ generalTranslations.cancel ?? "Cancel" }}</span>
                                </button>

                                <button
                                    v-if="action !== null && !hideConfirmButton"
                                    type="button"
                                    id="confirm_button"
                                    class="btn-rounded"
                                    :class="disabled ? 'disabled' : 'action'"
                                    :disabled="disabled"
                                    @click="triggerAction"
                                >
                                    <span>{{ processedAction }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { v4 as uuidv4 } from "uuid";
import vClickOutside from "v-click-outside";
import modalLayerManager from "../services/modal-layer-manager";
Vue.use(vClickOutside);

const defaultFunction = () => {};

export default {
    props: {
        appendToBody: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            default: null,
        },
        modalName: {
            type: String,
            default: "",
        },
        title: {
            type: String,
            default: "",
        },
        modalStyle: {
            type: String,
            default: "",
        },
        action: {
            type: String,
            default: "Confirm",
        },
        active: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noTitle: {
            type: Boolean,
            default: false,
        },
        fade: {
            type: Boolean,
            default: true,
        },
        keepAlive: {
            type: Boolean,
            default: false,
        },
        showCancel: {
            type: Boolean,
            default: false,
        },
        hideCancelButton: {
            type: Boolean,
            default: false,
        },
        hideCloseButton: {
            type: Boolean,
            default: false,
        },
        showModalFooter: {
            type: Boolean,
            default: true,
        },
        showModalFooterButton: {
            type: Boolean,
            default: true,
        },
        // Hides only the confirm button; the footer (and its border) still renders.
        hideConfirmButton: {
            type: Boolean,
            default: false,
        },
        bordered: {
            type: Boolean,
            default: false,
        },
        showModalBanner: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            generatedModalId: this.id ?? `bo-popup-modal_${uuidv4()}`,
            visible: this.active,
            modalLayer: null,
            layerZIndex: null,
            vcoConfig: {
                handler: this.deactivate,
                middleware: this.ignoreLoadingElement,
            },
            generalTranslations: window?.bo?.generalTranslations ?? {},
        };
    },
    computed: {
        modalId: {
            get() {
                return this.id ?? this.generatedModalId;
            },
        },
        modalTitleId: {
            get() {
                return `${this.modalId}_title`;
            },
        },
        processedAction() {
            switch (this.action) {
                case "Confirm":
                    return window?.bo?.generalTranslations?.confirm ?? "Confirm";

                case "Update":
                    return window?.bo?.generalTranslations?.update ?? "Update";

                default:
                    return this.action;
            }
        },
    },
    inject: {
        deactivatePopupModalValidation: {
            from: "deactivatePopupModalValidation",
            default: defaultFunction,
        },
    },
    provide() {
        return {
            // set a property or method that can be access from another component by using provide/inject methods
            triggerPopupModalAction: this.triggerAction,
        };
    },
    watch: {
        active: function (n, o) {
            // document.body.classList.toggle("modal-open");
            this.visible = n;
        },
        visible: function (n, o) {
            if (n) {
                this._acquireModalLayer();
            } else {
                this.$nextTick(() => {
                    if (!this.visible) this._releaseModalLayer();
                });
            }
            this.$emit(n ? "activate" : "deactivate");
        },
    },
    mounted() {
        if (this.appendToBody && this.$el?.parentNode !== document.body) {
            document.body.appendChild(this.$el);
        }

        // The modal can be mounted already visible (e.g. rendered behind a v-if by
        // bo-cell-button-popup), in which case the `visible` watcher never fires and
        // the layer would never be acquired, leaving the modal without a z-index.
        if (this.visible) {
            this._acquireModalLayer();
        }
    },
    beforeDestroy() {
        this._releaseModalLayer();

        if (this.appendToBody && this.$el?.parentNode === document.body) {
            document.body.removeChild(this.$el);
        }
    },
    methods: {
        activate() {
            this.visible = true;
            this.$emit("activate");
        },
        deactivate() {
            // trigger function once
            const result = this.deactivatePopupModalValidation;

            // compare function with default function to determine whether function injected from parent
            if (typeof result === "function" && result !== defaultFunction) {
                // get function result
                const actionResult = result();

                if (actionResult) {
                    this.visible = false;
                    this.$emit("deactivate");
                }
            } else {
                this.visible = false;
                this.$emit("deactivate");
            }
        },
        toggle() {
            this.visible ? this.deactivate() : this.activate();
        },
        triggerAction() {
            this.$emit("trigger");
            if (this.keepAlive == false) {
                this.deactivate();
            }
        },
        ignoreLoadingElement(event) {
            if (event.target.classList.contains("loading")) return false;
            return true;
        },
        _acquireModalLayer() {
            if (this.modalLayer) return;

            this.modalLayer = modalLayerManager.acquire(this.$el);
            this.layerZIndex = this.modalLayer.zIndex;

            this.$nextTick(() => {
                const modalElement = this.$el.querySelector(".modal");
                modalLayerManager.setSurface(this.modalLayer, modalElement);
            });
        },
        _releaseModalLayer() {
            if (!this.modalLayer) return;

            modalLayerManager.release(this.modalLayer);
            this.modalLayer = null;
            this.layerZIndex = null;
        },
    },
};
</script>
